import type { CopilotMessage } from "./types";

// Live turns, kept outside React so a reply keeps streaming when the user
// opens another chat (or leaves the page). The backend cancels a turn when
// its connection closes, so the connection has to outlive the page's
// conversation view. Each running conversation gets an entry; the view that
// shows it subscribes, and the entry is dropped once the turn ends — after
// that, history is the source of truth again.

export interface CopilotStream {
  messages: CopilotMessage[];
  title: string;
  abort: AbortController;
  /** Backend id of the assistant row being written (for server cancel). */
  assistantDbId: number | null;
  /** The turn's ``client_message_id``. */
  clientMessageId: string;
}

type Listener = () => void;

const streams = new Map<number, CopilotStream>();
// Final messages of a turn that ended before any view subscribed (a fast
// reply, or one that finished while the view was still mounting).
const finished = new Map<number, CopilotMessage[]>();
const listeners = new Map<number, Set<Listener>>();

const notify = (conversationId: number) => {
  listeners.get(conversationId)?.forEach((listener) => listener());
};

export const copilotStreams = {
  get: (conversationId: number | null | undefined) =>
    conversationId == null ? undefined : streams.get(conversationId),

  isStreaming: (conversationId: number | null | undefined) =>
    conversationId != null && streams.has(conversationId),

  begin: (conversationId: number, stream: CopilotStream) => {
    streams.set(conversationId, stream);
    notify(conversationId);
  },

  update: (
    conversationId: number,
    update: (messages: CopilotMessage[]) => CopilotMessage[]
  ) => {
    const stream = streams.get(conversationId);
    if (!stream) return;
    stream.messages = update(stream.messages);
    notify(conversationId);
  },

  setAssistantDbId: (conversationId: number, id: number) => {
    const stream = streams.get(conversationId);
    if (stream) stream.assistantDbId = id;
  },

  /** The turn ended. Subscribers get one last notify with the entry gone. */
  end: (conversationId: number, stream: CopilotStream) => {
    if (streams.get(conversationId) !== stream) return;
    streams.delete(conversationId);
    finished.set(conversationId, stream.messages);
    notify(conversationId);
  },

  /** Claim the final messages of a turn that has ended, once. */
  takeFinished: (conversationId: number) => {
    const messages = finished.get(conversationId);
    finished.delete(conversationId);
    return messages;
  },

  /** Someone is looking at this conversation right now. */
  isWatched: (conversationId: number) =>
    (listeners.get(conversationId)?.size ?? 0) > 0,

  subscribe: (conversationId: number, listener: Listener) => {
    const set = listeners.get(conversationId) ?? new Set<Listener>();
    set.add(listener);
    listeners.set(conversationId, set);
    return () => {
      set.delete(listener);
      if (set.size === 0) listeners.delete(conversationId);
    };
  },

  /** Tests only. */
  reset: () => {
    streams.forEach((stream) => stream.abort.abort());
    streams.clear();
    finished.clear();
    listeners.clear();
  },
};
